// Headless storefront analytics bridge → Shopify Custom Pixel + GA4.
//
// On a fully headless site, Shopify's Web Pixel sandbox isn't auto-loaded by
// the theme. Our Custom Pixel (installed in Shopify Admin) listens to
// `window.dataLayer.push(...)` events with a "farveladeland" namespace and
// re-publishes them as standard Shopify events.
//
// Server-safe: every call no-ops when `window` is undefined (SSR, loaders).

type DLItem = Record<string, unknown> & { event: string; ts: number };

declare global {
  interface Window {
    dataLayer?: DLItem[];
    gtag?: (...args: unknown[]) => void;
  }
}

const NS = "farveladeland";

export const GA_MEASUREMENT_ID = "G-0JVM6REXGT";

import { getConsent, hasConsent, onConsentChange, type ConsentValue } from "./consent";

// ---------- Consent signals in the dataLayer ----------
//
// Every visitor gets the consent state pushed to `window.dataLayer` (local to
// the page — nothing leaves the browser by doing this), so GTM or any future
// tag can read it:
//   1. Google Consent Mode commands: gtag("consent", "default" | "update").
//   2. A plain event per decision, for triggers / variables:
//        { event: "cookie_consent_default" | "cookie_consent_update",
//          consent_necessary, consent_preferences, consent_statistics,
//          consent_marketing: "granted" | "denied", consent_timestamp }
//      "default" fires on every page load with the stored choice (all denied
//      before a decision); "update" fires whenever the visitor saves a choice.
//
// Category → Consent Mode mapping:
//   preferences → functionality_storage, personalization_storage
//   statistics  → analytics_storage
//   marketing   → ad_storage, ad_user_data, ad_personalization
//
// ---------- Google Analytics, loaded only after consent ----------
//
// gtag.js is NOT loaded until the visitor consents to "statistics". Danish
// guidance (Datatilsynet) treats Consent Mode's cookieless pings as requiring
// consent too, so nothing is sent to Google before that. If consent is later
// withdrawn we set Google's opt-out flag (which blocks every hit), flip
// Consent Mode to denied and the consent module deletes the GA cookies.

let gaBootstrapped = false;
let gaLoaded = false;

function ensureGtagStub() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments as unknown as DLItem);
    };
  }
}

type Signal = "granted" | "denied";
const sig = (on: boolean | undefined): Signal => (on ? "granted" : "denied");

function consentModeSignals(state: ConsentValue) {
  return {
    ad_storage: sig(state?.marketing),
    ad_user_data: sig(state?.marketing),
    ad_personalization: sig(state?.marketing),
    analytics_storage: sig(state?.statistics),
    functionality_storage: sig(state?.preferences),
    personalization_storage: sig(state?.preferences),
  };
}

function pushConsentEvent(kind: "default" | "update", state: ConsentValue) {
  window.dataLayer!.push({
    event: `cookie_consent_${kind}`,
    ts: Date.now(),
    consent_necessary: "granted",
    consent_preferences: sig(state?.preferences),
    consent_statistics: sig(state?.statistics),
    consent_marketing: sig(state?.marketing),
    consent_timestamp: state ? new Date(state.ts).toISOString() : null,
  });
}

function loadGtag(state: ConsentValue) {
  gaLoaded = true;
  const gtag = window.gtag!;
  gtag("set", "ads_data_redaction", !state?.marketing);

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);

  gtag("js", new Date());
  // We fire page_view manually on every SPA route change via trackPageView,
  // so disable gtag's automatic first-load page_view to avoid double-counting.
  gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });
}

function onConsentUpdated(state: ConsentValue) {
  // Google's opt-out flag stops every hit, including the ping gtag would
  // otherwise send to report the consent change itself.
  (window as unknown as Record<string, boolean>)[`ga-disable-${GA_MEASUREMENT_ID}`] =
    !state?.statistics;
  window.gtag!("consent", "update", consentModeSignals(state));
  window.gtag!("set", "ads_data_redaction", !state?.marketing);
  pushConsentEvent("update", state);

  if (!gaLoaded && state?.statistics) {
    loadGtag(state);
    // Consent given mid-visit: count the page they're on.
    trackPageView({ locale: document.documentElement.lang === "en" ? "en" : "da" });
  }
}

export function bootstrapAnalytics() {
  if (gaBootstrapped || typeof window === "undefined") return;
  gaBootstrapped = true;
  ensureGtagStub();

  // Consent defaults MUST be in the dataLayer before any Google tag loads.
  const stored = getConsent();
  window.gtag!("consent", "default", {
    ...consentModeSignals(stored),
    security_storage: "granted",
  });
  pushConsentEvent("default", stored);

  if (stored?.statistics) loadGtag(stored);
  onConsentChange(onConsentUpdated);
}

function gtagForward(event: string, payload: Record<string, unknown>) {
  if (typeof window === "undefined" || !gaLoaded || !hasConsent("statistics")) return;
  const map: Record<string, string> = {
    page_viewed: "page_view",
    product_viewed: "view_item",
    collection_viewed: "view_item_list",
    product_list_viewed: "view_item_list",
    product_clicked: "select_item",
    search_submitted: "search",
    product_added_to_cart: "add_to_cart",
    product_removed_from_cart: "remove_from_cart",
    cart_viewed: "view_cart",
    wishlist_added: "add_to_wishlist",
    checkout_started: "begin_checkout",
    newsletter_signed_up: "generate_lead",
    showroom_booking_started: "generate_lead",
    showroom_booking_completed: "generate_lead",
    lamp_request_submitted: "generate_lead",
    sticky_cta_clicked: "select_promotion",
    scroll_depth: "scroll",
    article_viewed: "select_content",
    experiment_viewed: "experiment_impression",
    product_guide_completed: "product_guide_completed",
  };
  const gaEvent = map[event] ?? event;
  window.gtag!("event", gaEvent, payload);
}

function push(event: string, payload: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  // GA only receives events while statistics consent is given.
  gtagForward(event, payload);

  // Shopify Custom Pixel forwarding is gated on marketing consent
  // since it relays to third-party ad pixels.
  if (!hasConsent("marketing")) return;
  const item = { event: `${NS}:${event}`, ts: Date.now(), ...payload };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(item);
  try {
    window.postMessage({ __farveladeland: true, ...item }, "*");
  } catch {
    /* noop */
  }
}

// ----- Public API -----

export function trackPageView(opts: { url?: string; title?: string; locale?: "da" | "en" }) {
  if (typeof window === "undefined") return;
  push("page_viewed", {
    url: opts.url ?? window.location.href,
    path: window.location.pathname,
    page_location: opts.url ?? window.location.href,
    page_path: window.location.pathname,
    page_title: opts.title ?? document.title,
    title: opts.title ?? document.title,
    referrer: document.referrer || undefined,
    locale: opts.locale,
  });
}

export type AnalyticsProduct = {
  id: string;
  handle: string;
  title: string;
  vendor?: string;
  productType?: string;
  price?: { amount: string; currencyCode: string };
  variantId?: string;
};

export function trackProductView(p: AnalyticsProduct) {
  push("product_viewed", { product: p });
}

export function trackCollectionView(c: {
  id: string;
  handle: string;
  title: string;
  productCount?: number;
}) {
  push("collection_viewed", c);
}

export function trackProductListView(opts: {
  list_id: string;
  list_name: string;
  item_count?: number;
}) {
  push("product_list_viewed", opts);
}

export function trackProductClick(p: AnalyticsProduct & { list_id?: string; list_name?: string; index?: number }) {
  push("product_clicked", { ...p });
}

export function trackSearch(query: string, resultCount?: number) {
  push("search_submitted", { query, resultCount });
}

export function trackAddToCart(input: { product: AnalyticsProduct; quantity: number }) {
  push("product_added_to_cart", input);
}

export function trackRemoveFromCart(input: { product: AnalyticsProduct; quantity: number }) {
  push("product_removed_from_cart", input);
}

export function trackCartView(opts: {
  itemCount?: number;
  totalValue?: { amount: string; currencyCode: string };
}) {
  push("cart_viewed", opts);
}

export function trackWishlistAdd(p: AnalyticsProduct) {
  push("wishlist_added", { product: p });
}

export function trackCheckoutStarted(opts: {
  cartId?: string;
  totalValue?: { amount: string; currencyCode: string };
  itemCount?: number;
}) {
  push("checkout_started", opts);
}

export function trackNewsletterSignup(source: string) {
  push("newsletter_signed_up", { source });
}

export function trackShowroomBookingStarted(source: string) {
  push("showroom_booking_started", { source });
}

export function trackShowroomBookingCompleted() {
  push("showroom_booking_completed", {});
}

export function trackLampRequest() {
  push("lamp_request_submitted", {});
}

export function trackStickyCtaClick(label: string) {
  push("sticky_cta_clicked", { label });
}

export function trackScrollDepth(percent: 25 | 50 | 75 | 100, path?: string) {
  push("scroll_depth", { percent, path: path ?? (typeof window !== "undefined" ? window.location.pathname : undefined) });
}

export function trackExperiment(experiment_id: string, variant_id: string) {
  push("experiment_viewed", { experiment_id, variant_id });
}

export function trackProductGuideCompleted(opts: {
  guide_id: string;
  answers: Record<string, string>;
  guides: string[];
}) {
  push("product_guide_completed", {
    guide_id: opts.guide_id,
    answers: Object.entries(opts.answers)
      .map(([k, v]) => `${k}:${v}`)
      .join("|"),
    recommended_guides: opts.guides.join("|"),
  });
}

export function trackArticleView(opts: {
  content_type: "blog" | "leksikon";
  slug: string;
  title: string;
  locale?: "da" | "en";
}) {
  push("article_viewed", {
    content_type: opts.content_type,
    item_id: opts.slug,
    item_name: opts.title,
    locale: opts.locale,
  });
}
